'use client';

import React, { useMemo, useCallback } from 'react';
import { extractBaseId } from 'src/hooks/filters/useTableFilterSelections';
import { useGetProviderCohortsQuery } from 'src/api/redux/providerCohorts';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import { TableVolumeButton } from 'src/components/Tables/components/TableVolumeButton';
import { VOLUME_SELECT_OPTIONS } from 'src/components/constants';
import NewSearchVolumeCell from 'src/components/Table/NewSearchVolumeCell';
import EncountersHeaderVolume from 'src/components/Table/EncountersHeaderVolume';
import TrendVolumeHeader from 'src/components/Table/TrendVolumeHeader';
import NewTrendVolumeCell from 'src/components/Table/NewTrendVolumeCell';

const EMPTY_COLUMN_IDS: string[] = [];

/**
 * @description Custom hook to get the volume columns for the table including the grouping model for volumes
 * @returns volumeColumns, optionColumns, overlayColumn, columnGroupingModel, createSecondaryTableColumns, createEmptyDrawerColumn
 */
const useEncounterColumns = (
  location: string,
  // Accept these values from the parent component instead of calling useTableFilterSelections
  {
    isTrending,
    columnIds = EMPTY_COLUMN_IDS,
    profileName,
  }: {
    setColumnId?: any;
    isTrending: boolean;
    columnIds?: string[]; // Changed from number[] to string[]
    profileName?: string;
  }
) => {
  const { data: providerCohorts } = useGetProviderCohortsQuery();
  const { data: filterSets } = useGetFilterSetsQuery({
    type: 'CODE',
  });

  const combinationCohortsFilterSets = useMemo(() => {
    if (!providerCohorts?.results || !filterSets?.results) return [];

    return [...providerCohorts.results, ...filterSets.results];
  }, [providerCohorts?.results, filterSets?.results]);

  const volumeColumns = useMemo(() => {
    if (!combinationCohortsFilterSets || !columnIds || columnIds.length === 0) {
      return [];
    }

    // Create columns for each composite key in columnIds
    const columns = [];
    columnIds.forEach((compositeKey) => {
      const baseId = extractBaseId(String(compositeKey)); // Ensure it's a string
      const numericId = Number(baseId);

      // Find the cohort/filter set with this base ID
      const cohort = combinationCohortsFilterSets.find(
        (c) => c?.id === numericId
      );

      if (cohort) {
        columns.push({
          field: String(compositeKey), // Use composite key as field ID
          headerName: cohort?.label,
          groupable: false,
          sortable: true,
          headerClassName: 'MuiDataGrid-lightHeader',
          cellClassName: 'MuiDataGrid-lightCell',
          renderHeader: (params) => (
            <EncountersHeaderVolume tableType={location} {...params} />
          ),
          renderCell: (params) => (
            <NewSearchVolumeCell
              {...params}
              searchId={String(compositeKey)} // Pass composite key as string
              location={location}
              profileName={profileName}
            />
          ),
          type: 'string',
          sortingOrder: ['desc', 'asc'] as any, // Volume columns: only desc/asc, no null state
          disableColumnMenu: true,
        });
      }
    });

    return columns;
  }, [combinationCohortsFilterSets, columnIds, location, profileName]);

  const optionColumns = useMemo(() => {
    // Don't return early - create all possible columns regardless of columnIds
    if (
      !VOLUME_SELECT_OPTIONS ||
      !columnIds.length ||
      !combinationCohortsFilterSets
    )
      return [];

    const columns = [];

    // Create option columns for each selected columnId
    columnIds.forEach((compositeKey) => {
      const baseId = extractBaseId(String(compositeKey));
      const cohortId = Number(baseId);

      const selectedCohort = combinationCohortsFilterSets.find(
        (cohort) => cohort.id === cohortId
      );

      if (selectedCohort) {
        VOLUME_SELECT_OPTIONS.forEach((option) => {
          const tableField = `option_${option.value}_${compositeKey}`; // Use composite key for field
          columns.push({
            field: tableField,
            headerName: option?.label,
            groupable: false,
            sortable: false,
            headerClassName: 'MuiDataGrid-lightHeader',
            cellClassName: 'MuiDataGrid-lightCell',
            renderHeader: (params) => <TrendVolumeHeader {...params} />,
            renderCell: (params) => (
              <NewTrendVolumeCell
                {...params}
                location={location}
                field={option.value}
                searchId={String(compositeKey)} // Use full composite key to match API response structure
              />
            ),
            type: 'string',
            width: 85,
            disableColumnMenu: true,
          });
        });
      }
    });

    return columns;
  }, [location, combinationCohortsFilterSets, columnIds]);

  /**
   * Creates secondary table columns with dynamic volume column sizing and drawer column
   * @param location - The table location identifier (e.g., 'lists', 'referrals')
   * @param columnVisibilityModel - Current column visibility state
   * @returns Complete array of secondary table columns
   */
  const createSecondaryTableColumns = useCallback(
    (location: string) => {
      // Create a map of volume columns by field for quick lookup
      const volumeColumnsMap = volumeColumns.reduce((acc, col) => {
        acc[col.field] = col;
        return acc;
      }, {});

      // Order columns based on columnIds order - include all columns,
      // DataGrid's controlled columnVisibilityModel handles visibility
      const orderedVolumeColumns =
        columnIds && columnIds.length > 0
          ? columnIds
              .filter((id) => volumeColumnsMap[id])
              .map((id) => volumeColumnsMap[id])
          : [];

      const hasVisibleVolumeColumns = orderedVolumeColumns.length > 0;

      // Allow volume columns to be resized via MUI's native column separators.
      // min/max must differ from width to unlock drag-to-resize on the pinned section boundary.
      const volumeColumnMinWidth = 100;
      const volumeColumnMaxWidth = 400;

      // Create custom volume columns with conditional width
      const customVolumeColumns = orderedVolumeColumns.map(
        (originalCol, index) => {
          const volumeColumnWidth =
            orderedVolumeColumns.length === 1 ? 260 : 200;

          // Add a special class to the first column
          const isFirstColumn = index === 0;
          const headerClass = isFirstColumn
            ? 'secondary-column-header secondary-first-column-header'
            : 'secondary-column-header';
          const cellClass = isFirstColumn
            ? 'secondary-column-cell secondary-first-column-cell'
            : 'secondary-column-cell';

          return {
            ...originalCol,
            width: volumeColumnWidth,
            minWidth: volumeColumnMinWidth,
            maxWidth: volumeColumnMaxWidth,
            flex: undefined, // Remove flex when using fixed width
            // Add custom classes for styling with teal borders
            headerClassName: headerClass,
            cellClassName: cellClass,
          };
        }
      );

      // Create drawer column
      const drawerColumn = {
        field: 'volume_drawer',
        headerName: '',
        flex: 1,
        minWidth: hasVisibleVolumeColumns ? 56 : 200,
        maxWidth: hasVisibleVolumeColumns ? 56 : 200,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
        resizable: false,
        // Add custom classes for styling with teal borders and force center content
        headerClassName: 'secondary-column-header',
        headerAlign: 'center' as const,
        align: 'center' as const,
        cellClassName: 'secondary-column-cell',

        // Use our custom component that has its own access to isTrending with forced centering
        renderHeader: () => (
          <TableVolumeButton
            hasVisibleVolumeColumns={hasVisibleVolumeColumns}
            location={location}
          />
        ),
        renderCell: () => null,
      };

      // Include option columns when trending is active and columnIds exist -
      // DataGrid's controlled columnVisibilityModel handles visibility
      const filteredOptionColumns = optionColumns
        .filter(() => isTrending && columnIds && columnIds.length > 0)
        .map((col) => ({
          ...col,
          // Add custom classes for styling with teal borders
          headerClassName: 'secondary-column-header',
          cellClassName: 'secondary-column-cell',
        }));

      // Always put the drawer column first in the secondary columns section
      // This ensures it gets the proper left border styling
      const result = [
        ...customVolumeColumns,
        ...filteredOptionColumns,
        drawerColumn,
      ];
      return result;
    },
    [volumeColumns, optionColumns, columnIds, isTrending]
  );

  return {
    volumeColumns,
    optionColumns,
    createSecondaryTableColumns,
    combinationCohortsFilterSets,
  };
};

export default useEncounterColumns;
